// src/domains/core/dashboard/hooks/useWidgetConfig.ts

import type { WidgetConfig, WidgetSize } from "../types/widget.types";

import { useCallback } from "react";

import { useDashboardStore } from "../store/dashboardStore";

/**
 * Hook für Widget-Konfiguration
 *
 * @param widgetId - Widget identifier
 *
 * @returns {object} Widget configuration and update functions
 *
 */
export function useWidgetConfig(widgetId: string) {
  const store = useDashboardStore();

  const widget = store.getWidget(widgetId);

  const setVisible = useCallback(
    (visible: boolean) => {
      store.setWidgetVisible(widgetId, visible);
    },
    [widgetId, store],
  );

  const setSize = useCallback(
    (size: WidgetSize) => {
      store.setWidgetSize(widgetId, size);
    },
    [widgetId, store],
  );

  const setOrder = useCallback(
    (order: number) => {
      store.setWidgetOrder(widgetId, order);
    },
    [widgetId, store],
  );

  const updateConfig = useCallback(
    (config: Partial<WidgetConfig>) => {
      store.updateWidgetConfig(widgetId, config);
    },
    [widgetId, store],
  );

  return {
    config: widget,
    isVisible: widget?.visible ?? false,
    setOrder,
    setSize,
    setVisible,
    updateConfig,
    widget,
  };
}
